<template>
    <div class="product__info">
        <h2 class="shop__heading product__heading" v-text="item.title"></h2>
        <div class="product__info__grouped-text">
            <div class="product__info__price__container">
                <h3 v-if="showOfferPrice" class="product__info__price__container__text" :class="{'showOfferPrice': showOfferPrice}" v-text="`${currency}${item.old_price}`"></h3>
                <h3 class="product__info__price__container__text offer" v-text="displayPrice"></h3>
            </div>
            <multiselect
                    class="product__info__quantity shop__multiselect"
                    v-model="quantity"
                    openDirection="below"
                    :searchable="false"
                    :allow-empty="false"
                    :show-labels="false"
                    :options="options"/>
        </div>
        <h3 class="product__info__subheading" v-text="$t('SHOP_PRODUCT_FREE_DELIVERY')"></h3>
        <p class="product__info__center-mobile shop__prefix" v-if="item.stock < 20" v-text="$t('SHOP_PRODUCT_LOW_STOCK')"></p>
        <div class="product__info__button-container">
            <button
                class="product__info__button"
                :class="isPaused ? 'product__info__button__disabled' : 'product__info__button__green'"
                :disabled="isPaused"
                v-text="$t('SHOP_PRODUCT_ADD')"
                @click="addToNextDelivery"></button>
            <button class="product__info__button" v-text="$t('SHOP_PRODUCT_VIEW')" @click="openSummaryModal"></button>
        </div>
        <div v-if="isPaused" class="product__info__border-mobile">
            <p class="product__info__delivery-text shop__prefix" v-text="$t('SHOP_SUBSCRIPTION_PAUSED')"></p>
        </div>
        <div v-if="!hasMultipleActiveSubscriptions && !isPaused" class="product__info__border-mobile">
            <p class="product__info__delivery-text shop__prefix" v-text="$t(nextDeliveryText)"></p>
            <p class="product__info__delivery-date">{{nextDeliveryDate}}</p>
            <router-link v-if="!isEarliestDate" :to="{name: 'account.delivery-schedule'}">
                <p class="shop__prefix underline my-2" v-text="$t('SHOP_CONFIRMATION_RECEIVE_SOONER')"></p>
            </router-link>
        </div>
        <text-dropdown :expand="true" :heading="$t('SHOP_PRODUCT_DESCRIPTION')">
            <div class="product__info__accordion-content">
                <p class="shop__paragraph mt-0 mb-6">{{item.description}}</p>
                <div v-if="item.attributes.materials" class="product__info__specifications">
                    <p class="product__info__specifications-header" v-text="`${$t('SHOP_PRODUCT_MATERIALS')}:`"></p>
                    <p class="shop__paragraph m-0">{{item.attributes.materials}}</p>
                </div>
                <div v-if="item.attributes.dimensions" class="product__info__specifications">
                    <p class="product__info__specifications-header" v-text="`${$t('SHOP_PRODUCT_DIMENSIONS')}:`"></p>
                    <p class="shop__paragraph m-0">{{item.attributes.dimensions}}</p>
                </div>
            </div>
        </text-dropdown>
        <text-dropdown :heading="$t('SHOP_DELIVERY')">
            <p class="product__info__accordion-content shop__paragraph">{{$t('SHOP_PRODUCT_DELIVERY_TEXT')}}</p>
        </text-dropdown>
        <router-link :to="{name: 'account.shop'}">
            <p class="product__info__back-button shop__prefix" v-text="$t('SHOP_PRODUCT_BACK')"></p>
        </router-link>
        <add-to-next-delivery-modal
            v-if="showAddToOrderModal"
            :item="item"
            :quantity="quantity"
            :orderSuccess="orderSuccess"
            :parentErrorHandler="errorHandler"
            @closed="closeAddToOrderModal"/>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import * as moment from 'moment';
import { SUBSCRIPTION_STATUS_PAUSED } from '@/constants/Subscription';
import { formatDisplayDate } from '@/helpers/DateHelpers';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import { turnScrollOff } from '@/helpers/ScrollHelpers.js';
import countriesJson from '@/resources/countries.json';
import AddToNextDeliveryModal from '@/components/modals/shop/AddToNextDeliveryModal';
import ShopService from '@/services/shop/ShopService.js';
import TextDropdown from './TextDropdown.vue';

let shopService = new ShopService();
export default {
    name: 'ProductInformation',
    components: { AddToNextDeliveryModal, Multiselect, TextDropdown },
    data() {
        return {
            quantity: 1,
            showSummaryModal: false,
            showAddToOrderModal: false,
            orderSuccess: false,
            errorHandler: new ErrorResponseHandler(),
        };
    },
    props: {
        item: {
            required: true,
            type: Object,
        },
    },
    computed: {
        ...mapGetters({
            hasMultipleActiveSubscriptions: 'subscription/hasMultipleActiveSubscriptions',
            activeSubscription: 'subscription/getActiveSubscription',
            nextAvailableDelivery: 'shop/getNextAvailableDeliveryDate',
        }),
        options() {
            const quantityOptions = Array.from({ length: 5 }, (_, i) => i + 1);
            return quantityOptions;
        },
        currency() {
            const country = countriesJson.find(country => country.currency.code === this.item.currency);
            return country.currency.symbol;
        },
        displayPrice() {
            let fract = this.item.price.split('.')[1];
            const price = Number(fract) > 0 ? this.item.price : Number(this.item.price);
            return `${this.currency}${price}`;
        },
        deliveryDate() {
            return formatDisplayDate(this.nextAvailableDelivery);
        },
        isEarliestDate() {
            const deliveryDate = moment(this.nextAvailableDelivery);
            const nineDaysTime = moment().add(9, 'days');
            const isEarliestDate = deliveryDate.isSameOrBefore(nineDaysTime);
            return isEarliestDate;
        },
        showOfferPrice() {
            return Number(this.item.old_price) > 0;
        },
        isPaused() {
            return this.activeSubscription && this.activeSubscription.status === SUBSCRIPTION_STATUS_PAUSED;
        },
        nextDeliveryText() {
            return this.item.available_from ? 'SHOP_PRODUCT_AVAILABLE_FROM' : 'SHOP_PRODUCT_NEXT_DELIVERY';
        },
        nextDeliveryDate() {
            return this.item.available_from ? formatDisplayDate(this.item.available_from) : this.deliveryDate;
        },
    },
    created() {
        this.$store.dispatch('subscription/fetchAllSubscriptions');
        this.$store.dispatch('address/fetchAllAddresses');
        this.$store.dispatch('shop/getNextDeliveryDate');
    },
    watch: {
        activeSubscription() {
            this.$store.dispatch('shop/getNextDeliveryDate');
        },
    },
    methods: {
        openSummaryModal() {
            this.$store.commit('shop/openOrderSummary');
            turnScrollOff(document);
        },
        addToNextDelivery() {
            if (!this.hasMultipleActiveSubscriptions) {
                this.updateOrder();
            }
            this.showAddToOrderModal = !this.showAddToOrderModal;
        },
        updateOrder() {
            this.orderSuccess = true;
            shopService.updateOrder(this.activeSubscription.id, { product_id: this.item.productId, quantity: this.quantity })
                .then(() => {
                    this.$store.dispatch('shop/fetchOrderSummary', true);
                })
                .catch(error => {
                    this.orderSuccess = false;
                    setTimeout(() => this.errorHandler.setResponse(error.response?.data), 0);
                });
        },
        closeAddToOrderModal() {
            this.showAddToOrderModal = !this.showAddToOrderModal;
        },
    },
};
</script>
